/* global YT, Vimeo */
export default class Video {
    static key = 'Video';

    constructor(el) {
        this.el = el;

        this.options = {
            controls: 1,
            muted: 0,
            autoplay: 0,
            loop: 0,
        };

        this.videoContainer = this.el.querySelector('.js-video');

        // 1. Récupération de l'URL brute depuis Craft
        const rawUrl = this.el.dataset.videoUrl;

        // 2. Extraction du fournisseur et de l'ID
        const videoData = this.parseUrl(rawUrl);

        if (!videoData) {
            console.error('URL vidéo non valide ou non supportée :', rawUrl);
            return; // On stoppe l'instanciation si l'URL est invalide
        }

        this.provider = videoData.provider;
        this.videoId = videoData.id;
        this.playerReady = false;

        Video.instances.push(this);

        if (this.videoId) {
            Video.loadScript(this.provider);
        }
    }

    /**
     * Analyse l'URL pour retourner le fournisseur et l'ID
     */
    parseUrl(url) {
        if (!url) return null;

        const normalizedUrl = String(url).trim();

        // Détection YouTube (aligné sur Link::getYoutubeId côté PHP)
        const youtubeRegExp =
            /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed\/?)?)|.*[?&]v=)|youtu\.be\/|youtube-nocookie\.com\/embed\/)([a-zA-Z0-9_-]{11})/i;
        const youtubeMatch = normalizedUrl.match(youtubeRegExp);
        if (youtubeMatch) {
            return { provider: 'youtube', id: youtubeMatch[1] };
        }

        // Détection Vimeo
        const vimeoRegExp =
            /(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
        const vimeoMatch = normalizedUrl.match(vimeoRegExp);
        if (vimeoMatch) {
            return { provider: 'vimeo', id: vimeoMatch[1] };
        }

        return null;
    }

    static loadScript(provider) {
        if (provider === 'youtube' && !Video.youtubeScriptIsLoading) {
            Video.youtubeScriptIsLoading = true;
            const script = document.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            document.body.appendChild(script);
        } else if (provider === 'vimeo' && !Video.vimeoScriptIsLoading) {
            Video.vimeoScriptIsLoading = true;
            const script = document.createElement('script');
            script.src = 'https://player.vimeo.com/api/player.js';
            // Quand le script Vimeo est chargé, on initialise les instances Vimeo
            script.onload = Video.initVimeoInstances;
            document.body.appendChild(script);
        }
    }

    init() {
        this.setOptions();

        this.initPlayer = this.initPlayer.bind(this);

        this.initPlayer();
    }

    initPlayer(event) {
        if (event) {
            this.el.removeEventListener('click', this.initPlayer);
        }

        if (this.provider === 'youtube') {
            this.initYouTubePlayer();
        } else if (this.provider === 'vimeo') {
            this.initVimeoPlayer();
        }
    }

    initYouTubePlayer() {
        this.player = new YT.Player(this.videoContainer, {
            height: '100%',
            width: '100%',
            videoId: this.videoId,
            playerVars: {
                rel: 0,
                autoplay: this.options.autoplay,
                controls: this.options.controls,
                loop: this.options.loop,
                playlist: this.options.loop ? this.videoId : undefined,
                disablekb: this.options.controls,
                mute: this.options.muted,
                playsinline: 1,
                fs: 0,
            },
            events: {
                onReady: () => {
                    this.playerReady = true;

                    if (this.options.muted) {
                        this.player.mute();
                    }

                    // On initie l'observateur une fois le player prêt
                    this.setupObserver();
                },
                onStateChange: (event) => {
                    if (event.data == YT.PlayerState.PLAYING) {
                        Video.pauseAll(this);
                    } else if (event.data == YT.PlayerState.ENDED) {
                        this.player.seekTo(0);
                        // Ne met en pause à la fin que s'il n'y a pas de loop
                        if (!this.options.loop) {
                            this.pause();
                        }
                    }
                },
            },
        });
    }

    initVimeoPlayer() {
        this.player = new Vimeo.Player(this.videoContainer, {
            id: this.videoId,
            autoplay: this.options.autoplay,
            controls: this.options.controls,
            muted: this.options.muted,
            loop: this.options.loop,
            playsinline: 1,
            keyboard: this.options.controls,
            fs: 0,
            responsive: true, // Recommandé pour Vimeo
        });

        this.player.ready().then(() => {
            this.playerReady = true;

            // On initie l'observateur une fois le player prêt
            this.setupObserver();
        });

        this.player.on('play', () => {
            Video.pauseAll(this);
        });

        this.player.on('ended', () => {
            this.player.setCurrentTime(0);
            // Ne met en pause à la fin que s'il n'y a pas de loop
            if (!this.options.loop) {
                this.pause();
            }
        });
    }

    setupObserver() {
        const observer = new IntersectionObserver(this.watch.bind(this), {
            rootMargin: '50px 0px 50px 0px',
        });
        observer.observe(this.el);
    }

    setOptions() {
        if ('noControls' in this.el.dataset) {
            this.options.controls = 0;
        }

        if ('autoplay' in this.el.dataset) {
            this.options.autoplay = 1;
        }

        if ('muted' in this.el.dataset) {
            this.options.muted = 1;
        }

        if ('loop' in this.el.dataset) {
            this.options.loop = 1;
        }
    }

    watch(entries) {
        if (!this.playerReady) return;

        const entry = entries[0];

        if (entry.isIntersecting) {
            // On relance UNIQUEMENT si c'est une vidéo décorative
            if (this.options.autoplay === 1) {
                this.play();
            }
        } else {
            // On met tout en pause pour sauver les performances quand ça sort de l'écran
            this.pause();
        }
    }

    // Méthode unifiée pour gérer la lecture selon l'API
    play() {
        if (!this.playerReady) return;

        if (this.provider === 'youtube') {
            this.player.playVideo();
        } else if (this.provider === 'vimeo') {
            this.player.play();
        }
    }

    // Méthode unifiée pour gérer la pause selon l'API
    pause() {
        if (!this.playerReady) return;

        if (this.provider === 'youtube') {
            this.player.pauseVideo();
        } else if (this.provider === 'vimeo') {
            this.player.pause();
        }
    }

    static initYouTubeInstances() {
        document.documentElement.classList.add('is-youtube-ready');

        for (let i = 0; i < Video.instances.length; i++) {
            const instance = Video.instances[i];
            if (instance.provider === 'youtube') instance.init();
        }
    }

    static initVimeoInstances() {
        document.documentElement.classList.add('is-vimeo-ready');

        for (let i = 0; i < Video.instances.length; i++) {
            const instance = Video.instances[i];
            if (instance.provider === 'vimeo') instance.init();
        }
    }

    static pauseAll(currentInstance) {
        for (let i = 0; i < Video.instances.length; i++) {
            const instance = Video.instances[i];
            // On s'assure de NE PAS couper la lecture d'une vidéo décorative (autoplay: 1)
            // lorsqu'une autre vidéo est cliquée
            if (
                instance.playerReady &&
                instance !== currentInstance &&
                instance.options.autoplay === 0
            ) {
                instance.pause();
            }
        }
    }
}

Video.instances = [];

// Callback global appelé automatiquement par le script YouTube
window.onYouTubeIframeAPIReady = Video.initYouTubeInstances;
